import { ChatThread } from './AiChatContext';
import { isEmptyChatThread } from './chatThreadUtils';

const STORAGE_KEY = 'ai-chat-threads-v2';
const DEPARTMENT_STORAGE_KEY = 'ai-chat-department';
const MAX_PERSISTED_THREADS = 10;
const MAX_THREAD_AGE_MS = 30 * 24 * 60 * 60 * 1000;

export interface StoredChatState {
  threads: Record<string, ChatThread>;
  activeThreadId: string | null;
  isOpen: boolean;
}

function getEmptyState(): StoredChatState {
  return {
    threads: {},
    activeThreadId: null,
    isOpen: false,
  };
}

function pruneChatState(state: StoredChatState): StoredChatState {
  const entries = Object.entries(state.threads).sort(([, a], [, b]) => b.updatedAt - a.updatedAt);
  const cutoff = Date.now() - MAX_THREAD_AGE_MS;
  const activeThreadId = state.activeThreadId;

  const persistableEntries = entries.filter(([, thread]) => !isEmptyChatThread(thread));
  const freshEntries = persistableEntries.filter(([, thread]) => thread.updatedAt >= cutoff);
  const limitedEntries = freshEntries.slice(0, MAX_PERSISTED_THREADS);
  const hasActiveThread = activeThreadId ? limitedEntries.some(([id]) => id === activeThreadId) : false;

  if (activeThreadId && !hasActiveThread) {
    const activeEntry = persistableEntries.find(([id]) => id === activeThreadId);

    if (activeEntry) {
      limitedEntries.pop();
      limitedEntries.push(activeEntry);
      limitedEntries.sort(([, a], [, b]) => b.updatedAt - a.updatedAt);
    }
  }

  const nextThreads = Object.fromEntries(limitedEntries);

  return {
    threads: nextThreads,
    activeThreadId: activeThreadId && nextThreads[activeThreadId] ? activeThreadId : null,
    isOpen: state.isOpen,
  };
}

export function loadChatState(): StoredChatState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return getEmptyState();
    }

    const parsed = JSON.parse(raw);
    const threads = typeof parsed?.threads === 'object' && parsed.threads ? parsed.threads : {};
    const activeThreadId = typeof parsed?.activeThreadId === 'string' ? parsed.activeThreadId : null;
    const isOpen = typeof parsed?.isOpen === 'boolean' ? parsed.isOpen : false;

    return pruneChatState({
      threads,
      activeThreadId,
      isOpen,
    });
  } catch {
    return getEmptyState();
  }
}

export function saveChatState(state: StoredChatState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pruneChatState(state)));
  } catch {
    // localStorage may be full or unavailable -- continue with in-memory state only.
  }
}

export function removeChatState(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // noop
  }
}

export function getChatStorageKey(): string {
  return STORAGE_KEY;
}

export function loadDepartment(): string | null {
  try {
    return localStorage.getItem(DEPARTMENT_STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

export function saveDepartment(department: string): void {
  try {
    localStorage.setItem(DEPARTMENT_STORAGE_KEY, department);
  } catch {
    // noop
  }
}
